import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, CalendarDays, FolderOpen, CalendarRange, X } from 'lucide-react';
import { ActiveDeductions } from './ActiveDeductions';
import { TodayScorecard } from './TodayScorecard';
import { MonthlyScorecard } from './MonthlyScorecard';
import { CustomRangeScorecard } from './CustomRangeScorecard';
import { LoanStage } from '../data/loans';
import { OfficerData } from '../data/officers';
type Scope = 'monthly' | 'today' | 'custom';
interface ScorecardViewProps {
  officer: OfficerData;
  onTileClick: (stage: LoanStage, scope: 'today' | 'mtd') => void;
  onSpeedBonusClick: (scope: 'today' | 'mtd') => void;
  onViewAllFiles: () => void;
  onCallTileClick: (metric: 'dials' | 'answer_rate', scope: 'today' | 'mtd') => void;
}
// Sensible defaults for the custom range — last 7 days ending today (May 14, 2025)
const DEFAULT_START = '2025-05-08';
const DEFAULT_END = '2025-05-14';
// Quick preset ranges
const PRESETS: {
  label: string;
  start: string;
  end: string;
}[] = [{
  label: 'Last 7 days',
  start: '2025-05-08',
  end: '2025-05-14'
}, {
  label: 'Last 14 days',
  start: '2025-05-01',
  end: '2025-05-14'
}, {
  label: 'Last 30 days',
  start: '2025-04-15',
  end: '2025-05-14'
}, {
  label: 'April 2025',
  start: '2025-04-01',
  end: '2025-04-30'
}];
export function ScorecardView({
  officer,
  onTileClick,
  onSpeedBonusClick,
  onViewAllFiles,
  onCallTileClick
}: ScorecardViewProps) {
  const [scope, setScope] = useState<Scope>('monthly');
  const [startDate, setStartDate] = useState(DEFAULT_START);
  const [endDate, setEndDate] = useState(DEFAULT_END);
  return <div className="space-y-6" data-id="element-929" data-mp="153zp7ytbscoc">
      {/* Top bar: scope toggle + view all */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-930" data-mp="111cvfsd3ghoi">
        <div className="inline-flex bg-white border border-gray-200 rounded-lg p-1 shadow-sm self-start" role="tablist" aria-label="Scorecard timeframe" data-id="element-931" data-mp="w1grtu8b6s9h">
          <ScopeButton active={scope === 'monthly'} onClick={() => setScope('monthly')} icon={<CalendarDays className="w-4 h-4" data-id="element-933" data-mp="ch8f8vt3i8cq" />} label="Monthly" data-id="element-932" data-mp="qghiyw0479zt" />
          <ScopeButton active={scope === 'today'} onClick={() => setScope('today')} icon={<Calendar className="w-4 h-4" data-id="element-935" data-mp="155x7s6g355cv" />} label="Today" data-id="element-934" data-mp="hei0dz9gbxur" />
          <ScopeButton active={scope === 'custom'} onClick={() => setScope('custom')} icon={<CalendarRange className="w-4 h-4" data-id="element-937" data-mp="ve14mgz56p1s" />} label="Custom Range" data-id="element-936" data-mp="1a48ggdun99ti" />
        </div>

        <button onClick={onViewAllFiles} className="flex items-center gap-2 bg-white border border-gray-200 hover:border-blue-300 hover:shadow-sm text-[#0a1e5e] font-bold text-sm px-4 py-2 rounded-lg transition-all self-start sm:self-auto" data-id="element-938" data-mp="10cukuh5tcu5n">
          <FolderOpen className="w-4 h-4" data-id="element-939" data-mp="3dkc59gi5dnwa" />
          View All Loan Files
        </button>
      </div>

      {/* Custom range picker */}
      <AnimatePresence data-id="element-940" data-mp="38l962coumbw8">
        {scope === 'custom' && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-941" data-mp="3mdp2gun8vbo6">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 sm:p-5" data-id="element-942" data-mp="33nqvyiz3hqmn">
              <div className="flex flex-col lg:flex-row gap-4 lg:items-end" data-id="element-943" data-mp="2ynbi7qawih45">
                {/* Date inputs */}
                <div className="flex flex-col sm:flex-row gap-3 flex-1 min-w-0" data-id="element-944" data-mp="iv5kckya5m73">
                  <DateField label="From" value={startDate} onChange={setStartDate} max={endDate} data-id="element-945" data-mp="dy5di7tlwfex" />
                  <DateField label="To" value={endDate} onChange={setEndDate} min={startDate} data-id="element-946" data-mp="1e5u09r2b84jj" />
                </div>

                {/* Presets */}
                <div className="flex flex-col gap-1.5 min-w-0" data-id="element-947" data-mp="1o2qb43fol2r9">
                  <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-948" data-mp="1s5mxm58cdj0h">
                    Quick Picks
                  </span>
                  <div className="flex flex-wrap gap-1.5" data-id="element-949" data-mp="21w7qrd58xvc0">
                    {PRESETS.map(p => {
                  const active = p.start === startDate && p.end === endDate;
                  return <button key={p.label} onClick={() => {
                    setStartDate(p.start);
                    setEndDate(p.end);
                  }} className={`text-xs font-bold px-2.5 py-1.5 rounded-md border transition-colors ${active ? 'bg-[#0a1e5e] text-white border-[#0a1e5e]' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-[#0a1e5e]'}`} data-id="element-950" data-mp="2buxbddoj1tvy">
                          {p.label}
                        </button>;
                })}
                  </div>
                </div>

                {/* Clear / reset */}
                <button onClick={() => {
              setStartDate(DEFAULT_START);
              setEndDate(DEFAULT_END);
            }} className="flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-[#0a1e5e] self-start lg:self-end px-2 py-1.5" data-id="element-951" data-mp="ix9wqz6yl9pb">
                  <X className="w-3.5 h-3.5" data-id="element-952" data-mp="16ofb19x93txi" />
                  Reset
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* The active scorecard */}
      <AnimatePresence mode="wait" data-id="element-953" data-mp="2w6lyz3z9pfft">
        <motion.div key={scope} initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.2
      }} data-id="element-954" data-mp="30634tyilweli">
          {scope === 'monthly' && <MonthlyScorecard officer={officer} onTileClick={stage => onTileClick(stage, 'mtd')} onSpeedBonusClick={() => onSpeedBonusClick('mtd')} onAnswerRateClick={() => onCallTileClick('answer_rate', 'mtd')} onDialsClick={() => onCallTileClick('dials', 'mtd')} data-id="element-955" data-mp="2hf3n9idfy729" />}
          {scope === 'today' && <TodayScorecard officer={officer} onTileClick={stage => onTileClick(stage, 'today')} onSpeedBonusClick={() => onSpeedBonusClick('today')} onAnswerRateClick={() => onCallTileClick('answer_rate', 'today')} onDialsClick={() => onCallTileClick('dials', 'today')} data-id="element-956" data-mp="6n0qu1t8wuya" />}
          {scope === 'custom' && <CustomRangeScorecard startDate={startDate} endDate={endDate} loans={officer.loans} officer={officer} onTileClick={stage => onTileClick(stage, 'mtd')} onSpeedBonusClick={() => onSpeedBonusClick('mtd')} onAnswerRateClick={() => onCallTileClick('answer_rate', 'mtd')} onDialsClick={() => onCallTileClick('dials', 'mtd')} data-id="element-957" data-mp="kt2maoan94fz" />}
        </motion.div>
      </AnimatePresence>

      {/* Active deductions (only renders if any exist) — at the bottom, under the speed bonus */}
      <ActiveDeductions deductions={officer.deductions} data-id="element-958" data-mp="368yc3iql43kx" />
    </div>;
}
function ScopeButton({
  active,
  onClick,
  icon,
  label
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return <button onClick={onClick} role="tab" aria-selected={active} className={`flex items-center gap-2 px-4 py-2 text-sm font-bold rounded-md transition-all ${active ? 'bg-[#0a1e5e] text-white shadow-sm' : 'text-gray-600 hover:text-[#0a1e5e] hover:bg-gray-50'}`} data-id="element-959" data-mp="3l8tsyg6ca9l">
      {icon}
      {label}
    </button>;
}
function DateField({
  label,
  value,
  onChange,
  min,
  max
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  min?: string;
  max?: string;
}) {
  return <label className="flex-1 min-w-0" data-id="element-960" data-mp="3kx1nnfgk6xcd">
      <span className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-961" data-mp="3pujn8f8os07p">
        {label}
      </span>
      <input type="date" value={value} min={min} max={max} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-semibold text-[#0a1e5e] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-962" data-mp="103wrru2mfsfm" />
    </label>;
}